import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import './ThumbnailVotePage.css';

function AnimatedViews({ count }) {
  const [display, setDisplay] = useState(0);
  const ref = useRef(null);

  useEffect(() => {
    if (!count) return;
    const duration = 1200;
    const start = performance.now();
    const step = (now) => {
      const t = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - t, 3); // ease-out cubic
      setDisplay(Math.floor(eased * count));
      if (t < 1) ref.current = requestAnimationFrame(step);
    };
    ref.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(ref.current);
  }, [count]);

  const format = (n) => {
    if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
    if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
    return String(n);
  };

  return (
    <span className="tv-views">
      {format(display)} views
    </span>
  );
}

let fpPromise = null;
async function getFingerprint() {
  if (!fpPromise) {
    fpPromise = import('@fingerprintjs/fingerprintjs').then(async (FP) => {
      const agent = await FP.load();
      const result = await agent.get();
      return result.visitorId;
    });
  }
  return fpPromise;
}

export default function ThumbnailVotePage() {
  const { slug } = useParams();
  const [test, setTest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fingerprintId, setFingerprintId] = useState(null);
  const [hasVoted, setHasVoted] = useState(false);
  const [voteChecked, setVoteChecked] = useState(false);
  const [ranking, setRanking] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const loadedSlug = useRef(null);
  const [popIndex, setPopIndex] = useState(null);
  const [shuffleOrder, setShuffleOrder] = useState(null);
  const [avgColors, setAvgColors] = useState({});
  const [loadingFaded, setLoadingFaded] = useState(false);
  const fakeViews = useRef(null);
  if (!fakeViews.current) {
    fakeViews.current = [
      Math.floor(Math.random() * 500000) + 100000,
      Math.floor(Math.random() * 40000) + 5000,
      Math.floor(Math.random() * 800) + 50,
    ];
  }
  const ordinalLabels = ['1st', '2nd', '3rd'];

  // Load fingerprint
  useEffect(() => {
    getFingerprint().then(setFingerprintId).catch(() => {});
  }, []);

  // Fetch test data
  useEffect(() => {
    if (loadedSlug.current === slug) return;
    loadedSlug.current = slug;

    fetch(`/api/thumbnail-tests?action=vote&slug=${encodeURIComponent(slug)}`)
      .then(r => { if (!r.ok) throw new Error('not found'); return r.json(); })
      .then(data => {
        setTest(data);
        // Shuffle display order (Fisher-Yates)
        const order = data.images.map((_, i) => i);
        for (let i = order.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [order[i], order[j]] = [order[j], order[i]];
        }
        setShuffleOrder(order);
      })
      .catch(() => setError('This test doesn\'t exist.'))
      .finally(() => setLoading(false));
  }, [slug]);

  // Extract average color from each thumbnail
  useEffect(() => {
    if (!test?.images) return;
    test.images.forEach((src, i) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = 16;
        canvas.height = 16;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, 16, 16);
        const data = ctx.getImageData(0, 0, 16, 16).data;
        let r = 0, g = 0, b = 0, count = 0;
        for (let p = 0; p < data.length; p += 4) {
          r += data[p]; g += data[p + 1]; b += data[p + 2]; count++;
        }
        r = Math.round(r / count);
        g = Math.round(g / count);
        b = Math.round(b / count);
        setAvgColors(prev => ({ ...prev, [i]: `${r}, ${g}, ${b}` }));
      };
      img.src = src;
    });
  }, [test?.images]);

  // Check if already voted
  useEffect(() => {
    if (!fingerprintId || !slug) return;
    fetch(`/api/thumbnail-tests?action=check-vote&slug=${encodeURIComponent(slug)}&fingerprintId=${encodeURIComponent(fingerprintId)}`)
      .then(r => r.json())
      .then(d => { if (d.hasVoted) setHasVoted(true); })
      .catch(() => {})
      .finally(() => setVoteChecked(true));
  }, [fingerprintId, slug]);

  const handlePick = useCallback((imageIndex) => {
    if (hasVoted || submitting) return;
    if (ranking.includes(imageIndex)) {
      setRanking(prev => prev.filter(i => i !== imageIndex));
      return;
    }
    setRanking(prev => [...prev, imageIndex]);
    setPopIndex(imageIndex);
    setTimeout(() => setPopIndex(null), 350);
  }, [hasVoted, submitting, ranking]);

  const handleSubmit = useCallback(async () => {
    if (!test || !fingerprintId || ranking.length !== test.images.length) return;
    setSubmitting(true);
    try {
      const res = await fetch('/api/thumbnail-tests?action=vote', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ slug, ranking, fingerprintId }),
      });
      if (res.ok || res.status === 409) {
        setHasVoted(true);
      } else {
        const data = await res.json();
        setError(data.error || 'Failed to submit vote');
      }
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }, [test, fingerprintId, ranking, slug]);

  const isReady = !loading && voteChecked;

  useEffect(() => {
    if (isReady && !loadingFaded) {
      const timer = setTimeout(() => setLoadingFaded(true), 400);
      return () => clearTimeout(timer);
    }
  }, [isReady, loadingFaded]);

  if (!loadingFaded) {
    return (
      <div className="tv-page">
        <div className="tv-container">
          <div className={`tv-loading${isReady ? ' tv-loading-out' : ''}`}>
            <div className="tv-loading-track"><div className="tv-loading-bar" /></div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !test) {
    return (
      <div className="tv-page">
        <div className="tv-container">
          <div className="tv-error">{error || 'Test not found.'}</div>
        </div>
      </div>
    );
  }

  if (test.status !== 'open') {
    return (
      <div className="tv-page">
        <div className="tv-container">
          <div className="tv-closed">This vote has ended. Thanks for your interest!</div>
        </div>
      </div>
    );
  }

  if (hasVoted) {
    return (
      <div className="tv-page">
        <div className="tv-container">
          <div className="tv-thankyou">

            <h2>Thanks for voting!</h2>
            <p>Your response has been recorded.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="tv-page">
      <div className="tv-container">
        <div className="tv-header tv-stagger" style={{ '--delay': '0s' }}>
          <p className="tv-instructions">
            Rank the thumbnails based on how clearly they explain the concept of the title - both visually and conceptually.
          </p>
        </div>

        <div className="tv-thumbnails">
          {(shuffleOrder || test.images.map((_, i) => i)).map((origIdx, displayIdx) => {
            const img = test.images[origIdx];
            const rankPos = ranking.indexOf(origIdx);
            const isPicked = rankPos !== -1;
            const viewCount = isPicked ? fakeViews.current[rankPos] : null;
            const color = avgColors[origIdx];
            const rankColors = ['255, 215, 0', '192, 192, 192', '205, 127, 50']; // gold, silver, bronze
            const glowColor = isPicked ? rankColors[rankPos] : color;
            const style = {
              ...(glowColor ? { '--glow': glowColor } : {}),
              transform: isPicked ? 'translateY(-8px)' : 'translateY(0)',
              '--delay': `${0.15 + displayIdx * 0.1}s`,
            };
            return (
              <div
                className={`tv-thumb-wrapper tv-stagger${isPicked ? ' tv-thumb-picked' : ''}${popIndex === origIdx ? ' tv-thumb-pop' : ''}`}
                key={origIdx}
                style={style}
                onClick={() => !submitting && handlePick(origIdx)}
              >
                <div className="tv-thumb">
                  <img src={img} alt={`Option ${origIdx + 1}`} className="tv-thumb-img" />
                </div>
                <div className="tv-thumb-meta">
                  <p className="tv-video-title">{test.title}</p>
                  <div className={`tv-views-slot${isPicked ? ' tv-views-slot-open' : ''}`}>
                    {isPicked && <><AnimatedViews count={viewCount} key={rankPos} /><span className="tv-placement">&nbsp;· {ordinalLabels[rankPos]} place</span></>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="tv-bottom tv-stagger" style={{ '--delay': '0.45s' }}>
          {ranking.length === test.images.length ? (
            <button className={`tv-submit-btn${submitting ? ' tv-submit-loading' : ''}`} onClick={handleSubmit} disabled={submitting}>
              <span className="tv-submit-fill" />
              <span className="tv-submit-text">{submitting ? 'Submitting...' : 'Submit Vote'}</span>
            </button>
          ) : (
            <p className="tv-pick-hint" key={ranking.length}>
              Pick your {ordinalLabels[ranking.length]} choice
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
